/**
 * Audit event type translations and styling for Italian UI.
 */

// Event type translations
export const eventTypeLabels = {
  // Authentication
  user_registered: 'Registrazione utente',
  user_login: 'Login utente',
  user_logout: 'Logout utente',
  email_verified: 'Email verificata',

  // Password
  password_changed: 'Password modificata',
  password_reset_requested: 'Reset password richiesto',
  password_reset_completed: 'Reset password completato',

  // Email
  email_change_requested: 'Cambio email richiesto',
  email_change_completed: 'Cambio email completato',

  // Profile
  profile_updated: 'Profilo aggiornato',

  // Hikes
  hike_created: 'Gita creata',
  hike_updated: 'Gita modificata',
  hike_deleted: 'Gita eliminata',
  hike_visibility_changed: 'Visibilità gita modificata',

  // Organizers
  hike_organizer_added: 'Organizzatore aggiunto',
  hike_organizer_removed: 'Organizzatore rimosso',
  hike_organizer_updated: 'Organizzatore modificato',

  // Participation
  hike_registration: 'Iscrizione gita',
  hike_unregistration: 'Disiscrizione gita',
  participation_approved: 'Partecipazione approvata',
  participation_rejected: 'Partecipazione rifiutata',

  // Favorites
  favorite_added: 'Preferito aggiunto',
  favorite_removed: 'Preferito rimosso',

  // Admin
  role_changed: 'Ruolo modificato',
  admin_export: 'Export dati',

  // Security
  rate_limit_exceeded: 'Limite richieste superato',
};

// Event type colors (Tailwind classes)
export const eventTypeColors = {
  // Authentication - Blue
  user_registered: 'bg-blue-100 text-blue-800',
  user_login: 'bg-blue-100 text-blue-800',
  user_logout: 'bg-blue-100 text-blue-800',
  email_verified: 'bg-blue-100 text-blue-800',

  // Password - Purple
  password_changed: 'bg-purple-100 text-purple-800',
  password_reset_requested: 'bg-purple-100 text-purple-800',
  password_reset_completed: 'bg-purple-100 text-purple-800',

  // Email - Indigo
  email_change_requested: 'bg-indigo-100 text-indigo-800',
  email_change_completed: 'bg-indigo-100 text-indigo-800',

  // Profile - Teal
  profile_updated: 'bg-teal-100 text-teal-800',

  // Hikes - Green
  hike_created: 'bg-green-100 text-green-800',
  hike_updated: 'bg-green-100 text-green-800',
  hike_deleted: 'bg-red-100 text-red-800',
  hike_visibility_changed: 'bg-green-100 text-green-800',

  // Organizers - Yellow
  hike_organizer_added: 'bg-yellow-100 text-yellow-800',
  hike_organizer_removed: 'bg-yellow-100 text-yellow-800',
  hike_organizer_updated: 'bg-yellow-100 text-yellow-800',

  // Participation - Cyan
  hike_registration: 'bg-cyan-100 text-cyan-800',
  hike_unregistration: 'bg-cyan-100 text-cyan-800',
  participation_approved: 'bg-emerald-100 text-emerald-800',
  participation_rejected: 'bg-red-100 text-red-800',

  // Favorites - Pink
  favorite_added: 'bg-pink-100 text-pink-800',
  favorite_removed: 'bg-pink-100 text-pink-800',

  // Admin - Orange
  role_changed: 'bg-orange-100 text-orange-800',
  admin_export: 'bg-orange-100 text-orange-800',

  // Security - Red
  rate_limit_exceeded: 'bg-red-100 text-red-800',
};

// Action translations
export const actionLabels = {
  create: 'Creazione',
  update: 'Modifica',
  delete: 'Eliminazione',
  login: 'Login',
  logout: 'Logout',
  verify: 'Verifica',
  request: 'Richiesta',
  approve: 'Approvazione',
  reject: 'Rifiuto',
  add: 'Aggiunta',
  remove: 'Rimozione',
  export: 'Export',
  block: 'Blocco',
};

// Action colors
export const actionColors = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  login: 'bg-blue-100 text-blue-800',
  logout: 'bg-gray-100 text-gray-800',
  verify: 'bg-purple-100 text-purple-800',
  request: 'bg-yellow-100 text-yellow-800',
  approve: 'bg-green-100 text-green-800',
  reject: 'bg-red-100 text-red-800',
  add: 'bg-green-100 text-green-800',
  remove: 'bg-red-100 text-red-800',
  export: 'bg-orange-100 text-orange-800',
  block: 'bg-red-100 text-red-800',
};

// Status translations
export const statusLabels = {
  success: 'Successo',
  failed: 'Fallito',
  denied: 'Negato',
};

// Status colors
export const statusColors = {
  success: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  denied: 'bg-orange-100 text-orange-800',
};

// Resource type translations
export const resourceTypeLabels = {
  user: 'Utente',
  hike: 'Gita',
  participation: 'Partecipazione',
  favorite: 'Preferito',
  organizer: 'Organizzatore',
  users: 'Utenti',
  audit_events: 'Eventi audit',
};

// Event type categories for grouped filtering
export const eventTypeCategories = {
  'Autenticazione': [
    'user_registered',
    'user_login',
    'user_logout',
    'email_verified',
  ],
  'Password': [
    'password_changed',
    'password_reset_requested',
    'password_reset_completed',
  ],
  'Email': [
    'email_change_requested',
    'email_change_completed',
  ],
  'Profilo': [
    'profile_updated',
  ],
  'Gite': [
    'hike_created',
    'hike_updated',
    'hike_deleted',
    'hike_visibility_changed',
  ],
  'Organizzatori': [
    'hike_organizer_added',
    'hike_organizer_removed',
    'hike_organizer_updated',
  ],
  'Partecipazione': [
    'hike_registration',
    'hike_unregistration',
    'participation_approved',
    'participation_rejected',
  ],
  'Preferiti': [
    'favorite_added',
    'favorite_removed',
  ],
  'Amministrazione': [
    'role_changed',
    'admin_export',
  ],
  'Sicurezza': [
    'rate_limit_exceeded',
  ],
};

// Helper function to get label with fallback
export const getEventTypeLabel = (eventType) => {
  return eventTypeLabels[eventType] || eventType;
};

export const getEventTypeColor = (eventType) => {
  return eventTypeColors[eventType] || 'bg-gray-100 text-gray-800';
};

export const getActionLabel = (action) => {
  return actionLabels[action] || action;
};

export const getActionColor = (action) => {
  return actionColors[action] || 'bg-gray-100 text-gray-800';
};

export const getStatusLabel = (status) => {
  return statusLabels[status] || status;
};

export const getStatusColor = (status) => {
  return statusColors[status] || 'bg-gray-100 text-gray-800';
};

export const getResourceTypeLabel = (resourceType) => {
  return resourceTypeLabels[resourceType] || resourceType;
};
